
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Pagination } from '../models/pagination';
import { Setting, SettingResponse } from '../models/setting';
import { BaseService } from '../shared/base.service';

@Injectable({
  providedIn: 'root'
})
export class SettingsService extends BaseService {

  private settingsUrl = this.apiUrl + '/settings';

  getSettings(pagination?: Pagination): Observable<SettingResponse> {
    let params = new HttpParams({});

    if (pagination) {
      params = new HttpParams({
        fromObject: {
          rows: pagination.per_page.toString(),
          page: pagination.current_page.toString()
        }
      });
    }

    return this.http.get<SettingResponse>(this.settingsUrl, { params: params })
      .pipe(
        tap(setting => this.log(`fetched settings`)),
        catchError(this.handleError<SettingResponse>('getSettings', <SettingResponse>{}))
      );
  }

  /** GET Setting by id. Will 404 if id not found */
  getSetting(id: number): Observable<SettingResponse> {
    if (+id === 0) {
      return observableOf(this.initializeSetting());
    }

    const url = `${this.settingsUrl}/${id}`;
    return this.http.get<SettingResponse>(url).pipe(
      tap(_ => this.log(`fetched setting id=${id}`)),
      catchError(this.handleError<SettingResponse>(`getSetting id=${id}`))
    );
  }



  saveSetting(setting: Setting): Observable<SettingResponse> {
    if (setting.id === 0) {
      return this.addSetting(setting);
    }

    return this.updateSetting(setting);
  }

  /** POST: add a new Setting to the server */
  addSetting(setting: Setting): Observable<SettingResponse> {
    return this.http.post<SettingResponse>(this.settingsUrl, setting, this.httpOptions).pipe(
      tap((newSetting: SettingResponse) => this.log(`added setting w/ id=${newSetting.setting.id}`)),
      catchError(this.handleError<SettingResponse>('addSetting', <SettingResponse>{}))
    );
  }

  /** PUT: update the Setting on the server */
  updateSetting(setting: Setting): Observable<SettingResponse> {
    if (setting.id === 0) {
      return observableOf(this.initializeSetting());
    }

    const url = `${this.settingsUrl}/${setting.id}`;

    return this.http.put<SettingResponse>(url, setting, this.httpOptions).pipe(
      tap(_ => this.log(`updated setting id=${setting.id}`)),
      catchError(this.handleError<SettingResponse>('updateSetting', <SettingResponse>{}, 'Setting nicht gespeichert!'))
    );
  }

  /** DELETE: delete Setting from the server */
  deleteSetting(setting: Setting | number): Observable<SettingResponse> {
    const id = typeof setting === 'number' ? setting : setting.id;
    const url = `${this.settingsUrl}/${id}`;

    return this.http.delete<SettingResponse>(url, this.httpOptions).pipe(
      tap(_ => this.log(`deleted setting id=${id}`)),
      catchError(this.handleError<SettingResponse>('deleteSetting'))
    );
  }

  /**
   *  Return an initialized object
   */
  initializeSetting(): SettingResponse {
    return new SettingResponse();
  }

  getServerVersion(): Observable<any> {
    const url = this.serverUrl + '/api/version';

    return this.http.get<any>(url);
  }
}
